import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AddressService } from "../../../services/address.service";
import { NotificationService } from "../../../../core/services/notification.service";
import { NotificationType } from "../../../../core/services/notification-type";
import { YandexAddressDto } from "../../../models/address/yandex-address.dto";

declare const ymaps: any;

@Component({
    selector: 'address-clarification',
    templateUrl: './address-clarification.component.html',
    styleUrls: ['./address-clarification.component.scss',
        '../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
        '../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
    ]
})

export class AddressClarificationComponent implements OnInit {
    @Input() height: number = 600;
    @Input() address: string;

    @Output() onCancelEvent: EventEmitter<void> = new EventEmitter();
    @Output() onContinueEvent: EventEmitter<YandexAddressDto> = new EventEmitter<YandexAddressDto>();

    latitude: string;
    longitude: string;

    result: string;

    latPattern = /^-?(?:90(?:[.,]0{1,8})?|(?:[0-8]?\d)(?:[.,]\d{1,8})?)$/;
    lonPattern = /^-?(?:180(?:[.,]0{1,8})?|(?:1[0-7]\d|[0-9]?\d)(?:[.,]\d{1,8})?)$/;

    center: number[] = [55.751952, 37.600739];
    zoom = 8;

    marker: number[] | null = null;

    private applyTimer: any = null;

    addressSuccess: boolean = false;
    yandexAddress: YandexAddressDto;

    loading: boolean = false;

    constructor(
        private addressService: AddressService,
        private notificationService: NotificationService
    ) {

    }

    ngOnInit(): void {

    }

    cancel() {
        if (this.onCancelEvent) {
            this.onCancelEvent.emit();
        }
    }

    save() {
        if (this.onContinueEvent) {
            this.onContinueEvent.emit(this.yandexAddress);
        }
    }

    applyCoords() {
        const lat = this.toNumber(this.latitude);
        const lon = this.toNumber(this.longitude);

        if (!this.isValidLatLon(lat, lon)) {
            this.marker = null;
            return;
        }

        this.center = [lat, lon];
        this.marker = [lat, lon];
        this.zoom = 15; // приближение после ввода

        this.updateAddressByCoords(lat, lon);
    }

    private toNumber(v: string): number {
        // поддержим "55,7558" и "55.7558"
        return parseFloat((v || '').trim().replace(',', '.'));
    }

    private isValidLatLon(lat: number, lon: number): boolean {
        return Number.isFinite(lat) && Number.isFinite(lon)
            && lat >= -90 && lat <= 90
            && lon >= -180 && lon <= 180;
    }

    private applyCoordsDebounced() {
        this.addressSuccess = false;
        this.result = null;
        if (this.applyTimer) clearTimeout(this.applyTimer);
        this.applyTimer = setTimeout(() => this.applyCoords(), 250);
    }

    onCoordInput(e: Event, kind: 'lat' | 'lon') {
        const input = e.target as HTMLInputElement;
        const cleaned = this.sanitizeCoord(input.value);

        // ВАЖНО: обновляем то, что реально видно в инпуте
        if (input.value !== cleaned) input.value = cleaned;

        // синхронизируем модель
        if (kind === 'lat') this.latitude = cleaned;
        else this.longitude = cleaned;

        this.applyCoordsDebounced();
    }

    sanitizeCoord(v: string): string {
        if (!v) return '';

        // 1) оставляем только цифры, минус, точку/запятую
        let s = v.replace(/[^\d\-,.]/g, '');

        // 2) минус только один и только в начале
        s = s.replace(/(?!^)-/g, '');

        // 3) разрешаем только один разделитель (точка ИЛИ запятая)
        const dot = s.indexOf('.');
        const comma = s.indexOf(',');
        const firstSep =
            dot === -1 ? comma :
                comma === -1 ? dot :
                    Math.min(dot, comma);

        if (firstSep !== -1) {
            const head = s.slice(0, firstSep + 1);
            const tail = s.slice(firstSep + 1).replace(/[.,]/g, '');
            s = head + tail;
        }

        // 4) (опционально) ограничим длину дробной части, напр. 8 знаков
        const m = s.match(/^(-?\d+)([.,](\d*))?$/);
        if (m) {
            const intPart = m[1];
            const fracPart = m[3] ? m[3] : '';
            const fracLimited = fracPart.slice(0, 8);
            s = m[2] ? `${intPart}${m[2][0]}${fracLimited}` : intPart;
        }

        return s;
    }

    onPasteCoords(event: ClipboardEvent) {
        let text: string = null;

        if (!!event.clipboardData && !!event.clipboardData.getData('text')) {
            text = event.clipboardData.getData('text').trim();
        }

        if (!text) return;

        // Ищем два числа в строке
        const nums = text.match(/-?\d+(?:[.,]\d+)?/g);

        if (nums && nums.length >= 2) {
            event.preventDefault(); // отменяем обычную вставку

            // Берём первые два числа
            const lat = nums[0].replace(',', '.');
            const lon = nums[1].replace(',', '.');

            // Записываем сразу в оба поля
            this.latitude = lat;
            this.longitude = lon;

            this.applyCoordsDebounced();
        }
    }

    private async updateAddressByCoords(lat: number, lon: number) {
        this.loading = true;
        this.addressService.getAddressByCoords(lat, lon).subscribe(res => {
            if (res.isSuccessful) {
                if (!!res.data) {
                    this.yandexAddress = res.data;
                    this.result = `Результат поиска: ${res.data.address}`;
                } else {
                    this.result = 'По данным координатам не удалось найти адрес.';
                    this.yandexAddress = new YandexAddressDto();
                    this.yandexAddress.latitude = this.toNumber(this.latitude);
                    this.yandexAddress.longitude = this.toNumber(this.longitude);
                }

                this.addressSuccess = true;
            } else {
                this.addressSuccess = false;
                this.notificationService.error({
                    title: 'Ошибка',
                    message: res.errorDescription,
                    notificationType: NotificationType.SweetAlert
                });
            }

            this.loading = false;
        }, (err) => {
            this.loading = false;
            this.addressSuccess = false;
            this.notificationService.error({
                title: 'Ошибка',
                message: err.statusText,
                notificationType: NotificationType.SweetAlert
            });
        });
    }
}